import {
  createIamClient,
  DeleteOperation,
  deleteRequestFactory,
  FindOperation,
  getRequestFactory,
  IdPathParameters,
  postRequestFactory,
  putRequestFactory,
  SaveOperation,
  SearchOperation,
  searchRequestFactory,
} from '@infuse/api/common';
import { CreateOrganisation, Organisation, UpdateOrganisation } from '@infuse/api/iam/organisation';
import { Permission } from '@infuse/api/iam/permission';
import { SetRanking } from '@infuse/api/iam/role';
import { AddPermissionsAction, CreateRole, Role, UpdateRole } from '@infuse/iam/role/type';
import { CreateMachineUser, CreateUser, UpdateUser, User } from '@infuse/iam/user/type';
import { MultiSelectOption } from '@infuse/ui/domain';

export interface Tenant {
  id: string;
  name: string;
  active: boolean;
  description?: string;
  externalId?: string;
  address?: string;
  postalCode?: string;
  city?: string;
  country?: string;
  region?: string;
  website?: string;
  email?: string;
  type?: string;
}

export type CreateTenant = Pick<
  Tenant,
  'name' | 'description' | 'externalId' | 'address' | 'postalCode' | 'city' | 'country' | 'region' | 'website' | 'email' | 'type'
>;
export type UpdateTenant = CreateTenant;

const client = createIamClient('/rest/tenants');

export const tenantPermissions = {
  SEARCH: 'iam.search-tenants',
  GET: 'iam.get-tenant',
  CREATE: 'iam.create-tenant',
  UPDATE: 'iam.update-tenant',
  DELETE: 'iam.delete-tenant',
  ACTIVATE: 'iam.activate-tenant',
  DEACTIVATE: 'iam.deactivate-tenant'
};

export const searchTenants: SearchOperation<Tenant> = searchRequestFactory<Tenant>(client, {
  permissionId: tenantPermissions.SEARCH
});
export const getTenant: FindOperation<Tenant, string> = getRequestFactory<Tenant, string>(client, {
  permissionId: tenantPermissions.GET
});

export const createTenant: SaveOperation<CreateTenant, never> = postRequestFactory<CreateTenant, never>(client, {
  pattern: '',
  permissionId: tenantPermissions.CREATE
});
export const updateTenant: SaveOperation<UpdateTenant, never> = putRequestFactory<UpdateTenant, never>(client, {
  pattern: '/{id}',
  permissionId: tenantPermissions.UPDATE
});
export const deleteTenant: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{id}',
  permissionId: tenantPermissions.DELETE
});

export const activateTenant: SaveOperation<never, never> = postRequestFactory<never, never>(client, {
  pattern: '/{id}/activate',
  permissionId: tenantPermissions.ACTIVATE
});
export const deactivateTenant: SaveOperation<never, never> = postRequestFactory<never, never>(client, {
  pattern: '/{id}/deactivate',
  permissionId: tenantPermissions.DEACTIVATE
});

export const tenantOrganisationPermissions = {
  SEARCH: 'iam.search-tenant-organisations',
  GET: 'iam.get-tenant-organisation',
  CREATE: 'iam.create-tenant-organisation',
  UPDATE: 'iam.update-tenant-organisation',
  DELETE: 'iam.delete-tenant-organisation',
  ACTIVATE: 'iam.activate-tenant-organisation',
  DEACTIVATE: 'iam.deactivate-tenant-organisation'
};

// Organisations
export const searchTenantOrganisations: SearchOperation<Organisation, IdPathParameters<string>> = searchRequestFactory<
  Organisation,
  IdPathParameters<string>
>(client, { pattern: '/{id}/organisations', permissionId: tenantOrganisationPermissions.SEARCH });

export const getTenantOrganisation: FindOperation<Organisation, string> = getRequestFactory<Organisation, string>(
  client,
  {
    pattern: '/{id}/organisations/{organisationId}',
    permissionId: tenantOrganisationPermissions.GET
  }
);

export const createTenantOrganisation: SaveOperation<CreateOrganisation, never> = postRequestFactory<
  CreateOrganisation,
  never
>(client, { pattern: '/{parentId}/organisations', permissionId: tenantOrganisationPermissions.CREATE });
export const updateTenantOrganisation: SaveOperation<UpdateOrganisation, never> = putRequestFactory<
  UpdateOrganisation,
  never
>(client, { pattern: '/{parentId}/organisations/{id}', permissionId: tenantOrganisationPermissions.UPDATE });
export const deleteTenantOrganisation: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{parentId}/organisations/{id}',
  permissionId: tenantOrganisationPermissions.DELETE
});

export const activateTenantOrganisation: SaveOperation<never, never> = postRequestFactory<never, never>(client, {
  pattern: '/{parentId}/organisations/{id}/activate',
  permissionId: tenantOrganisationPermissions.ACTIVATE
});

export const deactivateTenantOrganisation: SaveOperation<never, never> = postRequestFactory<never, never>(client, {
  pattern: '/{parentId}/organisations/{id}/deactivate',
  permissionId: tenantOrganisationPermissions.DEACTIVATE
});

export const tenantRolePermissions = {
  SEARCH: 'iam.search-tenant-roles',
  GET: 'iam.get-tenant-role',
  SEARCH_COMPACT: 'iam.search-tenant-roles-compact',
  CREATE: 'iam.create-tenant-role',
  UPDATE: 'iam.update-tenant-role',
  DELETE: 'iam.delete-tenant-role',
  PROMOTE: 'iam.promote-tenant-role',
  DEMOTE: 'iam.demote-tenant-role',
  SET_RANKING: 'iam.set-tenant-role-ranking',
  SEARCH_PERMISSIONS: 'iam.search-role-permissions',
  ADD_PERMISSIONS: 'iam.add-permissions-to-role'
};

// Roles
export const searchTenantRoles: SearchOperation<Role, IdPathParameters<string>> = searchRequestFactory<
  Role,
  IdPathParameters<string>
>(client, { pattern: '/{id}/roles', permissionId: tenantRolePermissions.SEARCH });

export const getTenantRole: FindOperation<Role, string> = getRequestFactory<Role, string>(client, {
  pattern: '/{id}/roles/{roleId}',
  permissionId: tenantRolePermissions.GET
});

export const createTenantRole: SaveOperation<CreateRole, IdPathParameters<string>> = postRequestFactory<
  CreateRole,
  never
>(client, { pattern: '/{id}/roles', permissionId: tenantRolePermissions.CREATE });

export const updateTenantRole: SaveOperation<UpdateRole, IdPathParameters<string>> = putRequestFactory<
  UpdateRole,
  never
>(client, { pattern: '/{id}/roles/{roleId}', permissionId: tenantRolePermissions.UPDATE });

export const getTenantRoleSelectOptions: FindOperation<MultiSelectOption[], string> = getRequestFactory<
  MultiSelectOption[],
  string
>(client, { pattern: '/{id}/roles/select-options', permissionId: tenantRolePermissions.SEARCH_COMPACT });

export const deleteTenantRole: DeleteOperation<never> = deleteRequestFactory(client, {
  pattern: '/{id}/roles/{roleId}',
  permissionId: tenantRolePermissions.DELETE
});

export const promoteTenantRole: SaveOperation<undefined, never> = postRequestFactory<undefined, never>(client, {
  pattern: '/{id}/roles/{roleId}/promote',
  permissionId: tenantRolePermissions.PROMOTE
});
export const demoteTenantRole: SaveOperation<undefined, never> = postRequestFactory<undefined, never>(client, {
  pattern: '/{id}/roles/{roleId}/demote',
  permissionId: tenantRolePermissions.DEMOTE
});
export const setTenantRoleRanking: SaveOperation<SetRanking, never> = postRequestFactory<SetRanking, never>(client, {
  pattern: '/{id}/roles/{roleId}/set-ranking',
  permissionId: tenantRolePermissions.SET_RANKING
});

export const searchTenantRolePermissions: SearchOperation<Permission> = searchRequestFactory<Permission>(client, {
  pattern: '/{id}/roles/{roleId}/permissions',
  permissionId: tenantRolePermissions.SEARCH_PERMISSIONS
});

export const addPermissionsToRole: SaveOperation<AddPermissionsAction, never> = postRequestFactory<
  AddPermissionsAction,
  never
>(client, { pattern: '/{id}/roles/{roleId}/add-permissions', permissionId: tenantRolePermissions.ADD_PERMISSIONS });

export const tenantUserPermissions = {
  SEARCH: 'iam.search-tenant-users',
  GET: 'iam.get-tenant-user',
  CREATE: 'iam.create-tenant-user',
  UPDATE: 'iam.update-tenant-user',
  DELETE: 'iam.delete-tenant-user',
  ADD_ROLES: 'iam.add-roles-to-tenant-user',
  ADD_USERS: 'iam.add-users-to-tenant',
  REMOVE_USERS: 'iam.remove-users-from-tenant',
  FORCE_CHANGE_PASSWORD: 'iam.force-change-password-tenant-user',
  FORGOT_PASSWORD: 'iam.forgot-password-tenant-user',
  ACTIVATE: 'iam.activate-tenant-user',
  DEACTIVATE: 'iam.deactivate-tenant-user'
};

// Users
export const searchTenantUsers: SearchOperation<User, IdPathParameters<string>> = searchRequestFactory<
  User,
  IdPathParameters<string>
>(client, { pattern: '/{id}/users', permissionId: tenantUserPermissions.SEARCH });
export const getTenantUser: FindOperation<User, string> = getRequestFactory<User, string>(client, {
  pattern: '/{id}/users/{userId}',
  permissionId: tenantUserPermissions.GET
});

export const createTenantUser: SaveOperation<Omit<CreateUser, 'roles'>, User> = postRequestFactory<
  Omit<CreateUser, 'roles'>,
  never
>(client, { pattern: '/{id}/users', permissionId: tenantUserPermissions.CREATE });
export const updateTenantUser: SaveOperation<Omit<UpdateUser, 'roles' | 'email'>, User> = putRequestFactory<
  Omit<UpdateUser, 'roles' | 'email'>,
  never
>(client, { pattern: '/{id}/users/{userId}', permissionId: tenantUserPermissions.UPDATE });
export const deleteTenantUser: DeleteOperation<never> = deleteRequestFactory<never>(client, {
  pattern: '/{id}/users/{userId}',
  permissionId: tenantUserPermissions.DELETE
});

export const addRolesToTenantUser: SaveOperation<{ roleIds: string[]; replace: boolean }, never> = postRequestFactory<
  {
    roleIds: string[];
    replace: boolean;
  },
  never
>(client, { pattern: '/{id}/users/{userId}/add-roles', permissionId: tenantUserPermissions.ADD_ROLES });

export const addUsersToTenant: SaveOperation<{ userIds: string[] }, never> = postRequestFactory<
  { userIds: string[] },
  never
>(client, { pattern: '/{id}/add-users', permissionId: tenantUserPermissions.ADD_USERS });

export const removeUsersFromTenant: SaveOperation<{ userIds: string[] }, never> = postRequestFactory<
  { userIds: string[] },
  never
>(client, { pattern: '/{id}/remove-users', permissionId: tenantUserPermissions.REMOVE_USERS });

export const forceChangeTenantUserPassword: SaveOperation<{ tempPassword: string }, never> = postRequestFactory<
  { tempPassword: string },
  never
>(client, {
  pattern: '/{id}/users/{userId}/force-change-password',
  permissionId: tenantUserPermissions.FORCE_CHANGE_PASSWORD
});

export const forgotTenantUserPassword: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{id}/users/{userId}/forgot-password',
  permissionId: tenantUserPermissions.FORGOT_PASSWORD
});

export const activateTenantUser: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{id}/users/{userId}/activate',
  permissionId: tenantUserPermissions.ACTIVATE
});

export const deactivateTenantUser: SaveOperation<void, never> = postRequestFactory<void, never>(client, {
  pattern: '/{id}/users/{userId}/deactivate',
  permissionId: tenantUserPermissions.DEACTIVATE
});

export const tenantPermissionPermissions = {
  SEARCH: 'iam.search-tenant-permissions',
  GRANT_PERMISSIONS: 'iam.grant-permissions-to-organisation'
};

// Permissions endpoints for tenants
export const searchTenantPermissions: SearchOperation<
  Permission,
  IdPathParameters<string>
> = searchRequestFactory<Permission>(client, {
  pattern: '/{id}/permissions',
  permissionId: tenantPermissionPermissions.SEARCH
});

export const grantPermissionsToTenant: SaveOperation<AddPermissionsAction, never> = postRequestFactory<
  AddPermissionsAction,
  never
>(client, {
  pattern: '/{id}/grant-permissions',
  permissionId: tenantPermissionPermissions.GRANT_PERMISSIONS
});

export const tenantMachineClientPermissions = {
  CREATE: 'iam.create-tenant-machine-client'
};

export const createTenantMachineClient: SaveOperation<Omit<CreateMachineUser, 'roles'>, User> = postRequestFactory<
  Omit<CreateMachineUser, 'roles'>,
  User
>(client, { pattern: '/{id}/machine-clients', permissionId: tenantMachineClientPermissions.CREATE });
